const _measureContext = document.createElement('canvas').getContext('2d', { alpha: false });
const _measureCache = new Map(); // Nicht OL Map, sondern JS Map

function measureTextPx(text, fontCss) {
  let fontCache = _measureCache.get(fontCss);

  if (!fontCache) {
    fontCache = new Map();
    _measureCache.set(fontCss, fontCache);
  }

  let cachedMetrics = fontCache.get(text);

  if (cachedMetrics) return cachedMetrics;

  _measureContext.font = fontCss;
  const measurement = _measureContext.measureText(text);
  const fontSizeMatch = fontCss.match(/(\d+(?:\.\d+)?)px/);
  const fallbackFontSizePx = fontSizeMatch ? parseFloat(fontSizeMatch[1]) : 12;
  const textHeightPx = (measurement.actualBoundingBoxAscent ?? 0) + (measurement.actualBoundingBoxDescent ?? 0) || fallbackFontSizePx;

  cachedMetrics = { width: measurement.width, height: textHeightPx };
  _measureCache.set(text, cachedMetrics);

  return cachedMetrics;
}

function rotatedAABB(rectWidthPx, rectHeightPx, rotationAngleRad) {
  const cosineAbsolute = Math.abs(Math.cos(rotationAngleRad));
  const sineAbsolute = Math.abs(Math.sin(rotationAngleRad));

  return {
    width: rectWidthPx * cosineAbsolute + rectHeightPx * sineAbsolute,
    height: rectWidthPx * sineAbsolute + rectHeightPx * cosineAbsolute,
  };
}

export function screenBoxFromGeometry(map, geometry) {
  const extent = geometry.getExtent();
  const [minX, minY, maxX, maxY] = extent;

  const extentCornersWorld = [
    [minX, minY],
    [minX, maxY],
    [maxX, minY],
    [maxX, maxY],
  ];

  const extentCornersPixels = extentCornersWorld.map((coordinate) => map.getPixelFromCoordinate(coordinate));

  const xVal = extentCornersPixels.map((pixel) => pixel[0]);
  const yVal = extentCornersPixels.map((pixel) => pixel[1]);

  const minPxX = Math.min(...xVal);
  const maxPxX = Math.max(...xVal);
  const minPxY = Math.min(...yVal);
  const maxPxY = Math.max(...yVal);

  const widthPx = Math.max(0, maxPxX - minPxX);
  const heightPx = Math.max(0, maxPxY - minPxY);

  return { widthPx, heightPx };
}

export function fitTextToBox({
  text,
  maxW: maxWidthPx,
  maxH: maxHeightPx,
  fontFamily,
  fontWeight,
  maxFontPx: maxFontSizePx,
  minFontPx: minFontSizePx,
  angleRad: rotationAngleRad,
}) {
  if (!text) return null;

  let currentFontSizePx = maxFontSizePx;
  let currentFontCss = `${fontWeight} ${currentFontSizePx}px ${fontFamily}`;
  let fullTextMetrics = measureTextPx(text, currentFontCss);
  let rotatedBoundingBox = rotatedAABB(
    fullTextMetrics.width,
    fullTextMetrics.height,
    rotationAngleRad
  );

  const scaleWidth = maxWidthPx / Math.max(1, rotatedBoundingBox.width);
  const scaleHeight = maxHeightPx / Math.max(1, rotatedBoundingBox.height);
  const initialFitFontSize = Math.floor(currentFontSizePx * Math.min(scaleWidth, scaleHeight));

  if (initialFitFontSize < currentFontSizePx) {
    currentFontSizePx = Math.max(minFontSizePx, initialFitFontSize);
    currentFontCss = `${fontWeight} ${currentFontSizePx}px ${fontFamily}`;
    fullTextMetrics = measureTextPx(text, currentFontCss);
    rotatedBoundingBox = rotatedAABB(
      fullTextMetrics.width,
      fullTextMetrics.height,
      rotationAngleRad
    );
  }

  const fullTextFitsWithinBox = rotatedBoundingBox.width <= maxWidthPx && rotatedBoundingBox.height <= maxHeightPx;

  if (fullTextFitsWithinBox) {
    return { resultText: text, fontPx: currentFontSizePx };
  }

  if (currentFontSizePx > minFontSizePx) {
    const adjustedFontSizePx = Math.max(
      minFontSizePx,
      Math.min(currentFontSizePx - 1, Math.floor(currentFontSizePx * 0.98))
    );

    if (adjustedFontSizePx < currentFontSizePx) {
      currentFontSizePx = adjustedFontSizePx;
      currentFontCss = `${fontWeight} ${currentFontSizePx}px ${fontFamily}`;
      fullTextMetrics = measureTextPx(text, currentFontCss);
      rotatedBoundingBox = rotatedAABB(
        fullTextMetrics.width,
        fullTextMetrics.height,
        rotationAngleRad
      );

      const fitsAfterAdjustment =
        rotatedBoundingBox.width <= maxWidthPx &&
        rotatedBoundingBox.height <= maxHeightPx;

      if (fitsAfterAdjustment) {
        return { resultText: text, fontPx: currentFontSizePx };
      }
    }
  }

  currentFontSizePx = Math.max(minFontSizePx, currentFontSizePx);
  currentFontCss = `${fontWeight} ${currentFontSizePx}px ${fontFamily}`;

  let lowIndex = 1;
  let highIndex = text.length;
  let bestFittingEllipsisText = '';

  while (lowIndex <= highIndex) {
    const middleIndex = (lowIndex + highIndex) >> 1;
    const ellipsisCandidate = text.slice(0, middleIndex) + '...';

    const candidateMetrics = measureTextPx(ellipsisCandidate, currentFontCss);
    const candidateBoundingBox = rotatedAABB(
      candidateMetrics.width,
      candidateMetrics.height,
      rotationAngleRad
    );

    const candidateFitsWithinBox =
      candidateBoundingBox.width <= maxWidthPx &&
      candidateBoundingBox.height <= maxHeightPx;

    if (candidateFitsWithinBox) {
      bestFittingEllipsisText = ellipsisCandidate;
      lowIndex = middleIndex + 1;
    } else {
      highIndex = middleIndex - 1;
    }
  }

  if (bestFittingEllipsisText) {
    return { resultText: bestFittingEllipsisText, fontPx: currentFontSizePx };
  }

  const singleCharEllipsisText = text.slice(0, 1) + '...';
  const singleCharMetrics = measureTextPx(singleCharEllipsisText, currentFontCss);
  const singleCharBoundingBox = rotatedAABB(
    singleCharMetrics.width,
    singleCharMetrics.height,
    rotationAngleRad
  );

  const singleCharFitsWithinBox =
    singleCharBoundingBox.width <= maxWidthPx &&
    singleCharBoundingBox.height <= maxHeightPx;

  if (singleCharFitsWithinBox) {
    return { resultText: singleCharEllipsisText, fontPx: currentFontSizePx };
  }

  return null;
}